// Zajednički pomoćni formateri za "Moj radni dan" dashboard.
// Backend šalje vreme u UTC - prikazujemo ga u CET/CEST (Evropa/Belgrade, Srbija).

export const TIME_ZONE = 'Europe/Belgrade';

export const MONTHS = [
  'januar', 'februar', 'mart', 'april', 'maj', 'jun',
  'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar',
];

export const WEEKDAYS = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];

export const DAY_NAMES = [
  'Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota',
];

// Prikaži vreme u CET/CEST (HH:mm)
export const formatTime = (dateString) => {
  if (!dateString) return '--:--';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '--:--';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

// Ključ datuma (YYYY-MM-DD) u CET/CEST vremenskoj zoni.
// Vraća null za nevalidan datum (npr. polje koje nije prosleđeno sa backenda).
export const dateKeyInZone = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

// Trenutno prikazani mesec kao { year, month } (month je 0-indeksiran)
export const getCurrentMonth = () => {
  const [y, m] = dateKeyInZone(new Date()).split('-').map(Number);
  return { year: y, month: m - 1 };
};

// Vrednost parametra "month" za /api/Shift/my-shifts u formatu MM-yyyy
export const formatMonthParam = ({ year, month }) =>
  `${String(month + 1).padStart(2, '0')}-${year}`;

// Pretvori ključ YYYY-MM-DD u UTC timestamp (za poređenje dana)
export const keyToUTC = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

// Srpska množina: vrati odgovarajući oblik reči
export const pluralForm = (count, one, few, many) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

// "Ponedeljak, 5. oktobar 2026."
export const formatLongDate = (date) => {
  const key = dateKeyInZone(date);
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  const dayName = DAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${dayName}, ${d}. ${MONTHS[m - 1]} ${y}.`;
};

// "5. oktobar"
export const formatShortDate = (value) => {
  const key = dateKeyInZone(value);
  if (!key) return '';
  const [, m, d] = key.split('-').map(Number);
  return `${d}. ${MONTHS[m - 1]}`;
};

// Relativna oznaka dana: Danas, Sutra, Prekosutra ili Za N dana
export const getRelativeDayLabel = (dateString) => {
  if (!dateString) return 'Sledeća smena';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Sledeća smena';
  const diffDays = Math.round(
    (keyToUTC(dateKeyInZone(date)) - keyToUTC(dateKeyInZone(new Date()))) / 86400000
  );
  if (diffDays <= 0) return 'Danas';
  if (diffDays === 1) return 'Sutra';
  if (diffDays === 2) return 'Prekosutra';
  return `Za ${diffDays} ${pluralForm(diffDays, 'dan', 'dana', 'dana')}`;
};

// Odbrojavanje do zadatog trenutka (sati, minuti, sekunde). `passed` je true
// kada je trenutak već prošao (diff <= 0). `now` se može proslediti radi testiranja.
export const getCountdown = (target, now = Date.now()) => {
  if (!target) return null;
  const date = new Date(target);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = date.getTime() - now;
  if (diffMs <= 0) return { hours: 0, minutes: 0, seconds: 0, passed: true };
  const totalSeconds = Math.floor(diffMs / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    passed: false,
  };
};

// Preostalo vreme do kraja smene (sati, minuti, sekunde)
export const getTimeRemaining = (endTime, now = Date.now()) => {
  const countdown = getCountdown(endTime, now);
  if (!countdown) return null;
  return {
    hours: countdown.hours,
    minutes: countdown.minutes,
    seconds: countdown.seconds,
    ended: countdown.passed,
  };
};

// Faza smene u odnosu na trenutno vreme:
//  - 'upcoming' - smena još nije počela (startTime je u budućnosti)
//  - 'ongoing'  - smena je u toku (startTime <= now < endTime)
//  - 'ended'    - smena je završena (endTime je prošao)
//  - null       - nema dovoljno podataka o vremenu smene
export const getShiftPhase = (shift, now = Date.now()) => {
  if (!shift) return null;
  const start = shift.startTime ? new Date(shift.startTime).getTime() : null;
  const end = shift.endTime ? new Date(shift.endTime).getTime() : null;
  const hasStart = start != null && !Number.isNaN(start);
  const hasEnd = end != null && !Number.isNaN(end);
  if (!hasStart && !hasEnd) return null;
  if (hasEnd && now >= end) return 'ended';
  if (hasStart && now < start) return 'upcoming';
  return 'ongoing';
};

// Datum u formatu dd.MM.yyyy (CET/CEST)
export const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

// Datum + vreme (dd.MM.yyyy HH:mm) u CET/CEST
export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${formatDate(date)} ${formatTime(date)}`;
};

// ===== Prikaz polja zadatka (koristi ih lista zadataka i desni panel sa detaljima) =====

// Ime korisnika (štićenika) - iz polja `residentName` ili iz naslova oblika "Naslov - Ime"
export const getResidentName = (task) => {
  if (task?.residentName) return task.residentName;
  const title = task?.title || '';
  const idx = title.lastIndexOf(' - ');
  return idx > -1 ? title.slice(idx + 3).trim() : '';
};

// Soba iz opisa ("soba 12") ili iz polja `roomName`
export const getRoomName = (task) => {
  const match = /soba\s*([0-9a-zA-Z-]+)/i.exec(task?.description || '');
  return match ? match[1] : task?.roomName || '';
};

// Naslov zadatka - naziv zadatka/aktivnosti koji backend šalje u `title`
export const getTaskTitle = (task) => (task?.title || '').trim() || 'Zadatak';

// Podnaslov: soba · korisnik · tip obroka/aktivnosti (bez ponavljanja dela koji je već u naslovu)
export const getTaskSubtitle = (task) => {
  const title = getTaskTitle(task).toLowerCase();
  const isNew = (value) => {
    const v = String(value || '').trim();
    return v && !title.includes(v.toLowerCase());
  };

  const parts = [];
  const room = getRoomName(task);
  if (room) parts.push(`Soba ${room}`);
  const resident = getResidentName(task);
  if (isNew(resident)) parts.push(resident);
  const label = task?.mealTypeName || task?.activityTypeName;
  if (isNew(label)) parts.push(label);
  return parts.join(' · ');
};

// ===== Nivo ozbiljnosti beleške (severity) =====
// 1 = Veoma važno, 2 = Važno, 3 = Normalno (isto kao kod beleški stanara)
export const SEVERITY_OPTIONS = [
  { value: 1, label: 'Veoma važno', tone: 'high', tagType: 'red' },
  { value: 2, label: 'Važno', tone: 'medium', tagType: 'magenta' },
  { value: 3, label: 'Normalno', tone: 'low', tagType: 'gray' },
];

// Tekstualna oznaka nivoa ozbiljnosti (npr. "Veoma važno")
export const severityLabel = (severity) => {
  const option = SEVERITY_OPTIONS.find((o) => o.value === Number(severity));
  return option ? option.label : '';
};

// Vizuelni ton nivoa ozbiljnosti: 'high' | 'medium' | 'low'
export const severityTone = (severity) => {
  const option = SEVERITY_OPTIONS.find((o) => o.value === Number(severity));
  return option ? option.tone : 'low';
};

// Carbon `Tag` tip za nivo ozbiljnosti: 'red' | 'magenta' | 'gray'
export const severityTagType = (severity) => {
  const option = SEVERITY_OPTIONS.find((o) => o.value === Number(severity));
  return option ? option.tagType : 'gray';
};

// ===== Paginacija beleški smene =====
// Podrazumevani broj beleški po strani.
export const NOTES_PAGE_SIZE = 5;

// Normalizuje `pagination` iz PagedResponse odgovora u jedinstveni oblik.
// Ako paginacije nema (npr. goli niz), vraća jednu stranu sa datim brojem.
export const normalizePagination = (body, fallbackCount = 0, pageSize = NOTES_PAGE_SIZE) => {
  const raw = body?.pagination || null;
  if (raw) {
    const totalCount = Number(raw.totalCount) || 0;
    const size = Number(raw.pageSize) || pageSize;
    const totalPages =
      Number(raw.totalPages) || Math.max(1, Math.ceil(totalCount / Math.max(1, size)));
    const pageNumber = Number(raw.pageNumber) || 1;
    return {
      pageNumber,
      pageSize: size,
      totalCount,
      totalPages,
      hasPreviousPage:
        typeof raw.hasPreviousPage === 'boolean' ? raw.hasPreviousPage : pageNumber > 1,
      hasNextPage:
        typeof raw.hasNextPage === 'boolean' ? raw.hasNextPage : pageNumber < totalPages,
    };
  }

  const totalPages = Math.max(1, Math.ceil(fallbackCount / Math.max(1, pageSize)));
  return {
    pageNumber: 1,
    pageSize,
    totalCount: fallbackCount,
    totalPages,
    hasPreviousPage: false,
    hasNextPage: false,
  };
};


